import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

import { BenchmarkListPagesHelper } from './benchmark-list-pages-helper';
import {
  TBenchmarkListFacets,
  TBenchmarkListMeta,
  TBenchmarkListQueryOptions,
  TTransferPricingBenchmarkWithServiceRequest,
} from './types';

export type TTransferPricingBenchmarksResponse = {
  benchmarks: TTransferPricingBenchmarkWithServiceRequest[];
  count: number;
  meta?: TBenchmarkListMeta;
  facets?: TBenchmarkListFacets;
};

function appendFilters(
  queryString: URLSearchParams,
  options: TBenchmarkListQueryOptions | undefined
): void {
  if (options?.includeFilterFacets)
    queryString.set('includeFilterFacets', 'true');

  const filters = options?.filters;
  if (!filters) return;

  filters.entityPairIds?.forEach((entityPairId) =>
    queryString.append('filterConfig[entityPairIds][]', entityPairId)
  );
  filters.testedPartyEntityIds?.forEach((testedPartyEntityId) =>
    queryString.append(
      'filterConfig[testedPartyEntityIds][]',
      String(testedPartyEntityId)
    )
  );
  filters.fiscalYears?.forEach((fiscalYear) =>
    queryString.append('filterConfig[fiscalYears][]', String(fiscalYear))
  );
  filters.benchmarkingTypes?.forEach((benchmarkingType) =>
    queryString.append('filterConfig[benchmarkingTypes][]', benchmarkingType)
  );

  if (filters.updatedAtStart)
    queryString.set('filterConfig[updatedAtStart]', filters.updatedAtStart);
  if (filters.updatedAtEnd)
    queryString.set('filterConfig[updatedAtEnd]', filters.updatedAtEnd);
}

export function useTransferPricingBenchmarks(
  companyId: number | null,
  listQuery?: TBenchmarkListQueryOptions
) {
  const queryString = new URLSearchParams();
  queryString.set('companyId', String(companyId));
  appendFilters(queryString, listQuery);

  const baseUrl = `transfer-pricing/benchmark?${queryString.toString()}`;
  const fetchPage = SWRFetcher({ method: 'GET' });

  const {
    data,
    error,
    mutate,
    isValidating,
  }: {
    data?: TTransferPricingBenchmarksResponse;
    error?: unknown;
    mutate: KeyedMutator<unknown>;
    isValidating: boolean;
  } = useSWR(
    companyId ? baseUrl : null,
    listQuery
      ? (url: string) =>
          BenchmarkListPagesHelper.fetchAll((cursor) =>
            fetchPage(
              cursor
                ? `${url}&paginationConfig[cursor]=${encodeURIComponent(cursor)}`
                : url
            )
          )
      : fetchPage,
    {
      keepPreviousData: Boolean(listQuery),
    }
  );

  return {
    data,
    error,
    mutate,
    isLoading: !data && !error,
    isValidating,
  };
}
